import { Http } from '@capacitor-community/http';
import { Capacitor } from '@capacitor/core';

// Only polyfill fetch on iOS native (not web)
if (Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform()) {
    console.log('🔧 Installing native HTTP fetch polyfill for iOS...');

    // Add global error handlers to catch silent failures
    window.addEventListener('unhandledrejection', (event) => {
        console.error('🚨 Unhandled Promise Rejection:', event.reason);
        console.error('🚨 Promise:', event.promise);
    });

    window.addEventListener('error', (event) => {
        console.error('🚨 Global Error:', event.error);
        console.error('🚨 Message:', event.message);
    });

    const originalFetch = window.fetch;
    const NATIVE_HTTP_TIMEOUT_MS = 20000;

    window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
        try {
            const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;

            // Skip polyfill for Firestore streaming connections (they need
            // original fetch), on firestore.googleapis.com or a local emulator.
            if (url.includes('/google.firestore.v1.Firestore/') && (url.includes('/Listen/channel') || url.includes('/Write/channel'))) {
                console.log(`⏭️ Skipping polyfill for Firestore streaming: ${url}`);
                return originalFetch(input, init);
            }

            // Skip polyfill for relative URLs (local files) - use original fetch
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
                console.log(`⏭️ Using original fetch for local file: ${url}`);
                return originalFetch(input, init);
            }

            // The native plugin rejects string bodies (e.g. JSON posts from
            // Firebase Auth / Functions) before sending anything, so those
            // always ended up on the original fetch after a wasted round trip.
            if (typeof init?.body === 'string') {
                return originalFetch(input, init);
            }

            console.log(`🌐 Native HTTP request: ${url}`);

            // Use Capacitor native HTTP. The plugin never settles a call that
            // fails at the network level, so bound it and fall back.
            const response = await Promise.race([
                Http.request({
                    url,
                    method: init?.method || 'GET',
                    headers: init?.headers as Record<string, string> || {},
                    data: init?.body,
                }),
                new Promise<never>((_, reject) =>
                    setTimeout(() => reject(new Error('Native HTTP timed out')), NATIVE_HTTP_TIMEOUT_MS)),
            ]);

            console.log(`✅ Native HTTP response: ${url} - Status: ${response.status}`);
            console.log(`📦 Response data type: ${typeof response.data}`);

            // Convert Capacitor response to standard Response object
            // Capacitor HTTP returns parsed data, we need to convert it back to string for Response
            let body: string;
            let contentType = 'text/plain';

            if (response.data !== null && response.data !== undefined) {
                if (typeof response.data === 'string') {
                    body = response.data;
                    contentType = response.headers?.['Content-Type'] || response.headers?.['content-type'] || 'text/plain';
                } else {
                    // Data is already parsed (JSON object), stringify it
                    body = JSON.stringify(response.data);
                    contentType = 'application/json';
                    console.log(`📝 Stringified JSON body length: ${body.length}`);
                }
            } else {
                body = '';
            }

            // Ensure Content-Type header is set for JSON responses
            const headers = new Headers(response.headers || {});
            if (!headers.has('Content-Type') && !headers.has('content-type')) {
                headers.set('Content-Type', contentType);
            }

            console.log(`📋 Creating Response with status ${response.status}, Content-Type: ${contentType}`);

            const fetchResponse = new Response(body, {
                status: response.status,
                statusText: response.status === 200 ? 'OK' : 'Error',
                headers: headers,
            });

            console.log(`✅ Response object created successfully`);

            return fetchResponse;
        } catch (error) {
            console.error('❌ Native HTTP error:', error);
            console.error('❌ Error details:', JSON.stringify(error));
            // Fallback to original fetch if native fails
            return originalFetch(input, init);
        }
    };

    console.log('✅ Native HTTP fetch polyfill installed');
}
